import { z } from 'zod'

import { idSchema, msTimestampSchema } from './common.ts'

export const WorkspaceKindEnum = z.enum(['personal', 'shared'])
export type WorkspaceKind = z.infer<typeof WorkspaceKindEnum>

export const WorkspaceSchema = z.object({
  id: idSchema,
  name: z.string().min(1),
  kind: WorkspaceKindEnum,
  createdByUserId: idSchema,
  createdAt: msTimestampSchema,
  updatedAt: msTimestampSchema
})
export type WorkspaceRow = z.infer<typeof WorkspaceSchema>

export const WorkspaceRoleEnum = z.enum(['admin', 'member'])
export type WorkspaceRole = z.infer<typeof WorkspaceRoleEnum>

export const WorkspaceMembershipSchema = z.object({
  workspaceId: idSchema,
  userId: idSchema,
  role: WorkspaceRoleEnum,
  createdAt: msTimestampSchema
})
export type WorkspaceMembershipRow = z.infer<typeof WorkspaceMembershipSchema>

// A workspace-scoped membership invite (role + workspace to join). Unrelated to
// schemas/invite.ts's InviteSchema, which gates initial beta platform access (RAL-104) and has
// no concept of a workspace or role.
export const WorkspaceInvitationStatusEnum = z.enum(['pending', 'accepted', 'revoked'])
export type WorkspaceInvitationStatus = z.infer<typeof WorkspaceInvitationStatusEnum>

export const WorkspaceInvitationSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  email: z.string().email(),
  role: WorkspaceRoleEnum,
  status: WorkspaceInvitationStatusEnum,
  invitedByUserId: idSchema,
  createdAt: msTimestampSchema,
  expiresAt: msTimestampSchema,
  acceptedAt: msTimestampSchema.nullable().default(null),
  acceptedByUserId: idSchema.nullable().default(null),
  revokedAt: msTimestampSchema.nullable().default(null)
})
export type WorkspaceInvitationRow = z.infer<typeof WorkspaceInvitationSchema>

export const WorkspacePlanEnum = z.enum(['free', 'paid'])
export type WorkspacePlan = z.infer<typeof WorkspacePlanEnum>

// Free plan seat cap named in RAL-76's outcome ("two total seats, including its owner").
export const FREE_PLAN_SEAT_LIMIT = 2

export const WorkspaceEntitlementSchema = z.object({
  workspaceId: idSchema,
  plan: WorkspacePlanEnum,
  seatLimit: z.number().int().positive(),
  updatedAt: msTimestampSchema
})
export type WorkspaceEntitlementRow = z.infer<typeof WorkspaceEntitlementSchema>

// RAL-81. A pending invitation holds a seat for 14 days, long enough to survive a vacation and
// short enough that an abandoned invite frees its seat without anyone having to remember it.
// Expiry is derived from `expiresAt` at read time — there is no `expired` status and no sweep,
// so a row never needs a cron to become correct.
export const WORKSPACE_INVITATION_TTL_MS = 14 * 24 * 60 * 60 * 1000

export const SendWorkspaceInvitationRequestSchema = z.object({
  email: z.string().trim().toLowerCase().pipe(z.string().email()),
  role: WorkspaceRoleEnum.default(WorkspaceRoleEnum.enum.member)
})
export type SendWorkspaceInvitationRequest = z.infer<typeof SendWorkspaceInvitationRequestSchema>

export const SeatUsageSchema = z.object({
  seatsUsed: z.number().int().nonnegative(),
  seatLimit: z.number().int().positive(),
  pendingCount: z.number().int().nonnegative()
})
export type SeatUsage = z.infer<typeof SeatUsageSchema>

export const WorkspaceMemberViewSchema = z.object({
  userId: idSchema,
  email: z.string(),
  role: WorkspaceRoleEnum,
  joinedAt: msTimestampSchema
})
export type WorkspaceMemberView = z.infer<typeof WorkspaceMemberViewSchema>

export const WorkspaceInvitationViewSchema = z.object({
  id: idSchema,
  email: z.string(),
  role: WorkspaceRoleEnum,
  invitedByEmail: z.string().nullable(),
  createdAt: msTimestampSchema,
  expiresAt: msTimestampSchema
})
export type WorkspaceInvitationView = z.infer<typeof WorkspaceInvitationViewSchema>

// `seats` is null for a member: RAL-84's matrix keeps seat counts and billing pressure admin-only,
// while the roster itself is readable by everyone in the workspace.
export const WorkspaceMembersResponseSchema = z.object({
  members: z.array(WorkspaceMemberViewSchema),
  invitations: z.array(WorkspaceInvitationViewSchema),
  seats: SeatUsageSchema.nullable()
})
export type WorkspaceMembersResponse = z.infer<typeof WorkspaceMembersResponseSchema>

export const InvitationSendOutcomeEnum = z.enum([
  'sent',
  'already_member',
  'already_invited',
  'over_capacity',
  'busy'
])
export type InvitationSendOutcome = z.infer<typeof InvitationSendOutcomeEnum>

export const InvitationAcceptOutcomeEnum = z.enum([
  'accepted',
  'already_member',
  'wrong_email',
  'already_accepted',
  'revoked',
  'expired',
  'over_capacity',
  'busy'
])
export type InvitationAcceptOutcome = z.infer<typeof InvitationAcceptOutcomeEnum>

export const InvitationRevokeOutcomeEnum = z.enum(['revoked', 'already_accepted', 'already_revoked', 'busy'])
export type InvitationRevokeOutcome = z.infer<typeof InvitationRevokeOutcomeEnum>

export const InvitationResendOutcomeEnum = z.enum(['resent', 'already_accepted', 'revoked', 'over_capacity', 'busy'])
export type InvitationResendOutcome = z.infer<typeof InvitationResendOutcomeEnum>

export const WorkspaceInvitationResponseSchema = z.object({ invitation: WorkspaceInvitationViewSchema })
export type WorkspaceInvitationResponse = z.infer<typeof WorkspaceInvitationResponseSchema>

export const AcceptInvitationResponseSchema = z.object({
  workspaceId: idSchema,
  workspaceName: z.string(),
  role: WorkspaceRoleEnum
})
export type AcceptInvitationResponse = z.infer<typeof AcceptInvitationResponseSchema>

// The `error.code` values POST /api/invitations/:id/accept answers with (RAL-81). A revoked or
// missing invitation is the exception: it is a bare 404 carrying the error handler's own code, so
// the caller maps that one by status.
export const InvitationAcceptErrorCodeEnum = z.enum([
  'wrong_email',
  'already_accepted',
  'expired',
  'over_capacity',
  'seat_change_in_progress'
])
export type InvitationAcceptErrorCode = z.infer<typeof InvitationAcceptErrorCodeEnum>

// RAL-121 board D's four invitation refusals. Separate from the error codes above because two
// codes (`expired` and the revoked 404) land on one screen: from the invitee's side "withdrawn"
// and "ran out" are the same fact — the invitation is no longer open, through nobody's fault.
export const InvitationRefusalEnum = z.enum(['wrong_address', 'already_in', 'no_longer_open', 'seat_gone'])
export type InvitationRefusal = z.infer<typeof InvitationRefusalEnum>
// RAL-250. What the /invitations/:id page may learn before anyone clicks Accept: enough to name
// the workspace and the person who invited them, and nothing else. No token, no member list, no
// seat counts, no workspace id, and deliberately not the invited address — the page shows the
// signed-in caller's own email instead, and a mismatch surfaces as accept's `wrong_email`.
export const InvitationPreviewStateEnum = z.enum(['pending', 'not_available'])
export type InvitationPreviewState = z.infer<typeof InvitationPreviewStateEnum>

// One shape for revoked, accepted, expired and unknown: the response must not tell a holder of a
// guessed id which of those it hit, the same way the accept route 404s all of them alike.
export const InvitationPreviewResponseSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal(InvitationPreviewStateEnum.enum.not_available) }),
  z.object({
    state: z.literal(InvitationPreviewStateEnum.enum.pending),
    workspaceName: z.string().min(1),
    invitedByEmail: z.string().nullable(),
    role: WorkspaceRoleEnum
  })
])
export type InvitationPreviewResponse = z.infer<typeof InvitationPreviewResponseSchema>

export const MemberRemoveOutcomeEnum = z.enum(['removed', 'not_a_member', 'last_admin', 'busy'])
export type MemberRemoveOutcome = z.infer<typeof MemberRemoveOutcomeEnum>

export const RemoveWorkspaceMemberResponseSchema = z.object({ success: z.literal(true), seats: SeatUsageSchema })
export type RemoveWorkspaceMemberResponse = z.infer<typeof RemoveWorkspaceMemberResponseSchema>
